'use strict';

import React, {
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef
} from 'react';
import {useSelector} from 'react-redux';
import {keys} from 'ts-transformer-keys';

import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import {LoadDataParameters} from '~/shared/src/utilities/modules/calendar';
import {
    Alignment,
    useHookFormDelay,
    Visibility
} from '~/shared/src/utilities/modules/form';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';

import Calendar, {
    ReferenceType as CalendarReferenceType
} from '~/frontend/src/components/base/Calendar';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import {
    ComponentProps as FormComponentProps,
    Variables
} from '~/frontend/src/components/base/Form';

import {StoreState} from '~/frontend/src/store';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {MessageResolver} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';


type DefaultProps = {

    /**
     * The CSS class name(s) to add to the wrapper.
     */
    classNames? : string[];

    /**
     * Is the calendar disabled?
     */
    disabled? : boolean;

    /**
     * How does the view to display as?
     */
    displayMode? : 'card' | 'field';

    /**
     * The number of text rows to be displayed within the calendar.
     */
    height? : number;

    /**
     * Should the calendar mark if dirty?
     */
    markDirty? : boolean;

    /**
     * The max number of text rows to be displayed within the calendar.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * The styles to style the wrapper with.
     */
    style? : React.CSSProperties;

    /**
     * The alignment of the title for the calendar.
     */
    titleAlignment? : Alignment;

    /**
     * The CSS class names to title the label with.
     */
    titleClassNames? : string[];

    /**
     * The styles to style the title with.
     */
    titleStyle? : React.CSSProperties;

    /**
     * The visibility of the title for the calendar.
     */
    titleVisibility? : Visibility;

    /**
     * The visibility of the field
     */
    visibility? : Visibility;

};
const defaultProps : Required<DefaultProps> = {
    classNames     : [ 'yn-field-wrapper', 'field' ],
    disabled       : false,
    displayMode    : 'field',
    height         : 0,
    markDirty      : false,
    maxHeight      : 0,
    style          : {},
    titleAlignment : 'left',
    titleClassNames: [ 'yn-field-label' ],
    titleStyle     : {},
    titleVisibility: 'visible',
    visibility     : 'visible'
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the component state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    name : string;

    id? : string;

    viewKey : string;

    dataContext : string;

    formUid : string;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and form contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     * Cypher- or SQL-based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];

    filters? : string;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    /**
     * Additional parameters for the add-item form.
     */
    additionalAddItemFormProps? : Partial<Omit<FormComponentProps, 'rootItemId' | 'rootItemKey' | 'rootItemType'>>;

    /**
     * The placeholders for messages translated by MessageCatalog.
     */
    messagePlaceholders? : Placeholders;

    /**
     * A resolver function for message templateStrings.
     *
     * @param templateString
     */
    messageTemplateStringResolver? : MessageResolver;

    /**
     * The title for the calendar.
     */
    title? : string;

    /**
     * PassTrough options
     */
    pt? : {
        root? : React.HTMLAttributes<HTMLDivElement> & { [ key : `data-${string}` ] : string };
    };

    variables? : Variables;

    onVariableChange? : ( value : { start : string; end : string; } | undefined, originalEvent : any ) => void;
    onIntermediateVariableChange? : ( value : { start : string; end : string; } | undefined, originalEvent : any ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


export type ReferenceType = CalendarReferenceType & {};


const CalendarView = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const {
                  additionalAddItemFormProps,
                  additionalElusiveDataContexts,
                  additionalParameters,
                  classNames,
                  dataContext,
                  disabled,
                  //displayMode,
                  filters,
                  formUid,
                  height,
                  id,
                  //markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  name,
                  onIntermediateVariableChange,
                  onVariableChange,
                  pt,
                  style,
                  title,
                  titleAlignment,
                  titleClassNames,
                  titleStyle,
                  titleVisibility,
                  uid,
                  variables,
                  viewKey
              }                     = props,
              elementId             = id ?? `${uid}_calendarView`,
              calendarUid           = `${uid}_calendar`,
              user                  = useSelector( ( state : StoreState ) => state.session?.user ),
              calendarProps         = useSelector( ( state : StoreState ) => {
                  let retVal : PanelProps | undefined = getSubPanelProps( calendarUid, state );

                  if ( Object.keys( retVal ).length === 0 ) {
                      retVal = undefined;
                  }

                  return retVal;
              } ),
              calendar              = useRef<CalendarReferenceType | null>( null ),
              timeoutId             = useRef<NodeJS.Timeout | undefined>(),
              messageResolver       = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      messagePlaceholders,
                      messageTemplateStringResolver
                  ]
              ),
              resolvedTitle         = useMemo(
                  () => {
                      return title ? messageResolver( title ) : undefined;
                  },
                  [
                      JSON.stringify( title ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              heightStyle           = useMemo(
                  () => {
                      const retVal = getHeightStyle( {}, height, maxHeight, 'hidden', 'left', 'hidden' );

                      retVal.minHeight = '20rem';

                      return retVal;
                  },
                  [
                      height,
                      maxHeight
                  ]
              ),
              onCalendarRangeChange = useCallback(
                  ( start : Date, end : Date ) => {
                      const newCalendarRange = {
                          start: convertValueToString( start, 'TIMESTAMP' ),
                          end  : convertValueToString( end, 'TIMESTAMP' )
                      };

                      if ( JSON.stringify( newCalendarRange ) !== JSON.stringify( variables?.[ name ] ) ) {

                          onIntermediateVariableChange?.( newCalendarRange, undefined );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }

                          if ( onVariableChange ) {
                              timeoutId.current = setTimeout( onVariableChange.bind( null, newCalendarRange, undefined ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      onVariableChange,
                      onIntermediateVariableChange
                  ]
              ),
              calendarEl            = useMemo(
                  () => {
                      return <Calendar
                          uid={calendarUid}
                          ref={calendar}
                          dataContext={dataContext}
                          formUid={formUid}
                          mode="embedded"
                          editable={!disabled}
                          viewKey={viewKey}
                          filters={filters ? JSON.parse( filters ) : undefined}
                          additionalQueryParameters={additionalParameters}
                          additionalAddItemFormProps={additionalAddItemFormProps}
                          additionalElusiveDataContexts={additionalElusiveDataContexts ?? []}
                          onRangeChange={onCalendarRangeChange}
                          {...calendarProps}
                      />;
                  },
                  [
                      additionalAddItemFormProps,
                      additionalElusiveDataContexts,
                      additionalParameters,
                      calendarProps,
                      calendarUid,
                      dataContext,
                      viewKey
                  ]
              );


        // TODO: non-autoHeight (height = 0) not working (e.g. height = 2)


        useImperativeHandle(
            forwardedRef,
            () => ( {
                dirty            : () => calendar.current?.dirty() ?? false,
                dirtyDataContexts: () => calendar.current?.dirtyDataContexts() ?? []
            } )
        );


        return useMemo(
            () => {
                return <div
                    {...pt?.root}
                    id={elementId}
                    key={elementId}
                    className="yn-form-view yn-form-view-calendar"
                    style={style}
                >
                    <LoadingMaskPanel
                        key={`${calendarUid}_loadingMask`}
                        forComponent={calendarUid}
                        className="yn-view-loadingmask yn-calendarview-loadingmask"
                        style={{ width: '100%' }}
                    >
                        <div
                            {...pt?.root}
                            id={elementId}
                            key={elementId}
                            className={`yn-view-wrapper yn-view-wrapper-calendar ${classNames.join( ' ' )}`}
                            style={{ ...heightStyle }}
                        >
                            {
                                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                ( title || titleVisibility === 'placeholder' ) &&
                                <div className={`yn-form-container-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility} ${titleClassNames.join( ' ' )}`} style={titleStyle}>{resolvedTitle ?? ' '}</div>
                            }
                            {calendarEl}
                        </div>
                    </LoadingMaskPanel>
                </div>;
            },
            [
                calendarEl,
                calendarUid,
                classNames,
                elementId,
                heightStyle,
                pt,
                resolvedTitle,
                style,
                title,
                titleAlignment,
                titleClassNames,
                titleStyle,
                titleVisibility
            ]
        );
    }
} );
CalendarView.defaultProps = defaultProps;
CalendarView.displayName = 'CalendarView';

export default CalendarView;
